import { select } from 'd3-selection';
import { timeFormat, timeParse } from 'd3-time-format';
import { min, max, extent } from 'd3-array';
import { scaleLinear, scaleTime } from 'd3-scale';
import { line, curveStep } from 'd3-shape';
import { axisBottom, axisLeft } from 'd3-axis';

const d3 = {
  select,
  timeFormat,
  timeParse,
  max,
  min,
  extent,
  scaleLinear,
  scaleTime,
  line,
  axisBottom,
  axisLeft,
  curveStep
}

const parseDate = d3.timeParse('%d.%m.%Y');
const formatDate = d3.timeFormat('%Y');

const defaultOptions = {
  margin: {
    top: 5,
    right: 5,
    bottom: 20,
    left: 20
  },
  ratio: 0.4,
  startDate: parseDate('01.01.2014'),
  endDate: parseDate('01.07.2018'),
  xTicks: 6,
  yTicks: 4,
  className: 'line-chart',
  radius: 2.5
}

class LineChart {
  constructor(selector, options) {
    if (!selector) {
      return console.log('you need to specify a root element.');
    }

    this.selector = selector;
    this.options = Object.assign({}, defaultOptions, options);
    this.parent = null;
  }

  parseData(data) {
    const { startDate } = this.options;

    return data.map((d, i) => {
      const date = new Date(startDate);
      date.setMonth(date.getMonth() + i * 6);
      const value = +d;

      return {
        date,
        value
      }
    });
  }

  getDimensions() {
    const { margin, ratio } = this.options;
    const { width } = this.parent.node().getBoundingClientRect();
    const height = width * ratio;
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    return { width, height, innerWidth, innerHeight };
  }

  initSVG = (width, height) => {
    const { margin } = this.options;
    return this.parent.append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);
  }

  clear() {
    this.parent.node().innerHTML = '';
    this.parent.classed(this.options.className, false);
  }

  renderLine(selection, xScale, yScale, data) {
    const line = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.value))
    
    selection
      .append('g')
      .classed('line-g', true)
      .append('path')
      .datum(data)
      .classed('line', true)
      .attr('d', line);
  }

  renderDots = (selection, xScale, yScale, data) => {
    selection
      .append('g')
      .classed('dots-g', true)
      .selectAll('.dot')
      .data(data)
      .enter()
      .append('circle')
      .classed('dot', true)
      .attr('cx', d => xScale(d.date))
      .attr('cy', d => yScale(d.value))
      .attr('r', this.options.radius);
  }

  renderGrid = (selection, xScale, yScale, height, width) => {
    const { startDate, endDate, yTicks } = this.options;
    
    const xGrid = d3.axisBottom(xScale)
      .tickSize(-height)
      .tickValues([startDate, endDate])
      .tickFormat('');
    
    const [yMin, yMax] = yScale.domain();

    const yGrid = d3.axisLeft(yScale)
      .tickSize(-width)
      .ticks(yMax < 6 ? yMax : yTicks)
      .tickFormat('');
    
    const gridContainer = selection
      .append('g')
      .classed('grid-g', true);

    // gridContainer
    //   .append('g')
    //   .classed('grid-x', true)
    //   .attr('transform', `translate(0, ${height})`)
    //   .call(xGrid)
    //   .selectAll('line, .domain')
    //   .classed('crisp', true);

    gridContainer
      .append('g')
      .classed('grid-y', true)
      .call(yGrid)
      .selectAll('line, .domain')
      .classed('crisp', true);
  }

  // offsetXAxisLabels(selection) {
  //   selection.each((d, i, nodes) => {
  //     const node = d3.select(nodes[i])
  //     const transform = node.attr('transform');
  //     const translateX = parseFloat(transform.split(',')[0].split('(')[1]);
  //     const offset = i === 0 ? 5 : -5;
  //     node.attr('transform', `translate(${translateX + offset}, 0)`);
  //   });
  // }

  renderAxis = (selection, xScale, yScale, height) => {
    const { startDate, endDate, xTicks, yTicks } = this.options;

    const xAxis = d3.axisBottom(xScale)
      .tickFormat(formatDate)
      .tickValues([startDate, endDate])
      .tickFormat((d, i) => i === 0 ? '2. Halbjahr 2013' : '1. Halbjahr 2018');
    
    const [yMin, yMax] = yScale.domain();

    const yAxis = d3.axisLeft(yScale)
      .ticks(yMax < 6 ? yMax : yTicks)
      .tickFormat(d => d.toFixed(0));

    const xAxisSVG = selection
      .append('g')
      .classed('axis-x', true)
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis);

    xAxisSVG
      .selectAll('.tick')
      .attr('text-anchor', (d, i) => i === 0 ? 'start' : 'end');
    
    xAxisSVG.selectAll('line, path')
      .classed('crisp', true);

    selection
      .append('g')
      .classed('axis-y', true)
      .call(yAxis)
      .selectAll('line, path')
      .classed('crisp', true);
  }

  destroy = () => {
    this.clear();
  }

  render = (origData) => {
    this.parent = d3.select(this.selector);

    if (!this.parent.node()) {
      return false;
    }

    this.clear();
    this.parent.classed(this.options.className, true);

    const { width, height, innerWidth, innerHeight } = this.getDimensions();
    const svg = this.initSVG(width, height);
    const data = this.parseData(origData);

    const xMin = d3.min(data, d => d.date);
    const xMax = d3.max(data, d => d.date);
    const yExtent = [0, d3.max(data, d => d.value) + 1];

    const xScale = d3.scaleTime().domain([xMin, xMax]).range([10, innerWidth - 10]).nice();
    const yScale = d3.scaleLinear().domain(yExtent).range([innerHeight, 0]).nice();

    svg.call(this.renderGrid, xScale, yScale, innerHeight, innerWidth);
    svg.call(this.renderAxis, xScale, yScale, innerHeight);
    svg.call(this.renderLine, xScale, yScale, data);
    svg.call(this.renderDots, xScale, yScale, data);
  }
}

export default LineChart;
